/**
 * List pages для Block 7 — Commission (2026-05-25).
 * Editor + print-views — TECHDEBT (окрема міні-порція).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  commissionContract,
  commissionTransfer,
  commissionSale,
  commissionSettlement,
  type CommissionContract,
  type CommissionGoodsTransfer,
  type CommissionSale,
  type CommissionSettlementReport,
} from '@/api/commission';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function CommissionContractsPage() {
  const list = useQuery({
    queryKey: ['commission-contract'],
    queryFn: () => commissionContract.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<CommissionContract>[]>(
    () => [
      { key: 'contract_number', header: '№', width: 150, sortValue: (r) => r.contract_number ?? '', cell: (r) => r.contract_number },
      { key: 'contract_date', header: 'Дата', width: 130, sortValue: (r) => r.contract_date ?? '', cell: (r) => r.contract_date },
      {
        key: 'direction', header: 'Сторона', width: 130,
        sortValue: (r) => r.direction ?? '',
        cell: (r) => (r.direction === 'RECEIVED' ? 'Прийнято' : 'Передано'),
      },
      { key: 'counterparty_id', header: 'Контрагент', sortValue: (r) => r.counterparty_id ?? '', cell: (r) => r.counterparty_id },
      {
        key: 'commission_rate_value', header: 'Ставка', width: 100, align: 'right',
        sortValue: (r) => Number(r.commission_rate_value),
        cell: (r) => `${fmt2(r.commission_rate_value)}${r.commission_rate_type === 'PERCENTAGE' ? '%' : ''}`,
      },
      { key: 'vat_handler', header: 'ПДВ', width: 130, sortValue: (r) => r.vat_handler ?? '', cell: (r) => r.vat_handler },
      {
        key: 'is_active', header: 'Активний', width: 100, align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Договори комісії</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<CommissionContract>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="contract_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function CommissionTransferPage() {
  const list = useQuery({
    queryKey: ['commission-transfer'],
    queryFn: () => commissionTransfer.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<CommissionGoodsTransfer>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'commission_contract_id', header: 'Договір', sortValue: (r) => r.commission_contract_id ?? '', cell: (r) => r.commission_contract_id },
      {
        key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Передача на комісію</h2>
      </div>
      <DataTable<CommissionGoodsTransfer>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function CommissionSalePage() {
  const list = useQuery({
    queryKey: ['commission-sale'],
    queryFn: () => commissionSale.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<CommissionSale>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'final_buyer', header: 'Покупець',
        sortValue: (r) => r.final_buyer_name ?? r.final_buyer_id?.slice(0, 8) ?? '—',
        cell: (r) => r.final_buyer_name ?? r.final_buyer_id?.slice(0, 8) ?? '—',
      },
      {
        key: 'total_sale_amount', header: 'Сума', width: 130, align: 'right',
        sortValue: (r) => Number(r.total_sale_amount),
        cell: (r) => fmt2(r.total_sale_amount),
      },
      {
        key: 'total_commission_amount', header: 'Комісія', width: 130, align: 'right',
        sortValue: (r) => Number(r.total_commission_amount),
        cell: (r) => fmt2(r.total_commission_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Комісійні продажі</h2>
      </div>
      <DataTable<CommissionSale>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function CommissionSettlementPage() {
  const list = useQuery({
    queryKey: ['commission-settlement'],
    queryFn: () => commissionSettlement.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<CommissionSettlementReport>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'period', header: 'Період', width: 130,
        sortValue: (r) => r.period_year * 100 + r.period_month,
        cell: (r) => `${String(r.period_month).padStart(2, '0')}.${r.period_year}`,
      },
      {
        key: 'total_sales_amount', header: 'Продажі', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_sales_amount),
        cell: (r) => fmt2(r.total_sales_amount),
      },
      {
        key: 'total_commission_amount', header: 'Комісія', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_commission_amount),
        cell: (r) => fmt2(r.total_commission_amount),
      },
      {
        key: 'total_to_remit', header: 'До розрахунку', width: 150, align: 'right',
        sortValue: (r) => Number(r.total_to_remit),
        cell: (r) => fmt2(r.total_to_remit),
      },
      {
        key: 'payment_status', header: 'Статус', width: 120,
        sortValue: (r) => r.payment_status ?? '',
        cell: (r) => {
          const v = r.payment_status;
          if (v === 'SETTLED') return '✓ розрах.';
          if (v === 'PARTIAL') return '◐ частк.';
          return '○ pending';
        },
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Звіти комісіонера</h2>
      </div>
      <DataTable<CommissionSettlementReport>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_number"
        initialSortDir="desc"
      />
    </>
  );
}
